/* HTML mocks of real AI Board screens.
 *
 * Designed for LEGIBILITY at a glance: every screen breaks content into
 * mini-sections with eyebrows + short bullet points + highlighted key
 * terms. No walls of paragraph text. A buyer scanning the demo should
 * grok each screen in 2-3 seconds.
 */

import type { ScreenKey } from './phases';
import type { Lang } from './voice';

const SAMPLE_PROMPT = {
  en: 'How should a B2B SaaS founder validate product-market fit in 30 days with a $50k seed?',
  es: '¿Cómo debe un fundador de B2B SaaS validar product-market fit en 30 días con $50k de seed?',
  pt: 'Como um fundador de B2B SaaS deve validar product-market fit em 30 dias com US$ 50 mil de seed?',
};

const MODELS = [
  { id: 'claude',  label: 'Claude',  color: '#e0a85a', time: '0.8s', cost: '$0.02', tokens: 340 },
  { id: 'gpt',     label: 'GPT-4o',  color: '#19c37d', time: '1.2s', cost: '$0.03', tokens: 420 },
  { id: 'gemini',  label: 'Gemini',  color: '#6b8af5', time: '1.6s', cost: '$0.02', tokens: 380 },
];

// Per-model, structured response (short bullets, not paragraphs)
const RESPONSES = {
  en: [
    {
      approach: 'Customer discovery first',
      points: [
        '<b>30 calls</b> in week 1',
        'Score by <b>willingness-to-pay</b> (1–10)',
        'Below 6 = wrong ICP, not wrong product',
      ],
    },
    {
      approach: 'Sell before you build',
      points: [
        'Ship a <b>Stripe link</b> before code',
        '<b>3 paid LOIs</b> in 14 days = PMF signal',
        "Can't close LOIs? No MVP fixes it",
      ],
    },
    {
      approach: 'Unit economics or die',
      points: [
        '<b>CAC payback &lt; 12 months</b> or kill it',
        'Week 2: trial-to-paid conversion',
        'Below <b>8%</b> = messaging, not product',
      ],
    },
  ],
  es: [
    {
      approach: 'Descubrimiento primero',
      points: [
        '<b>30 llamadas</b> la semana 1',
        'Score por <b>willingness-to-pay</b> (1–10)',
        'Menos de 6 = ICP malo, no producto malo',
      ],
    },
    {
      approach: 'Vende antes de construir',
      points: [
        'Envía un <b>link de Stripe</b> antes del código',
        '<b>3 LOIs pagadas</b> en 14 días = señal PMF',
        '¿No cierras LOIs? Ningún MVP arregla eso',
      ],
    },
    {
      approach: 'Unit economics o muerte',
      points: [
        '<b>CAC payback &lt; 12 meses</b> o mátalo',
        'Semana 2: conversión trial-a-pago',
        'Bajo <b>8%</b> = mensaje, no producto',
      ],
    },
  ],
  pt: [
    {
      approach: 'Descoberta primeiro',
      points: [
        '<b>30 ligações</b> na semana 1',
        'Avalie por <b>disposição a pagar</b> (1–10)',
        'Abaixo de 6 = ICP errado, não produto',
      ],
    },
    {
      approach: 'Venda antes de construir',
      points: [
        'Envie um <b>link Stripe</b> antes do código',
        '<b>3 LOIs pagas</b> em 14 dias = sinal PMF',
        'Não fecha LOIs? Nenhum MVP resolve',
      ],
    },
    {
      approach: 'Unit economics ou morte',
      points: [
        '<b>Payback de CAC &lt; 12 meses</b> ou mate',
        'Semana 2: conversão trial-para-pago',
        'Abaixo de <b>8%</b> = mensagem, não produto',
      ],
    },
  ],
};

// Synthesized executive summary — broken into mini-sections
const SYNTH = {
  en: [
    { eyebrow: 'WEEK 1 — DISCOVERY', body: '<b>30 calls</b> scored by willingness-to-pay. Cluster top decile.' },
    { eyebrow: 'WEEK 2 — CLOSE',     body: 'Convert top 5 to <b>paid LOIs</b> in 14 days. <b>3+ closed</b> = PMF signal.' },
    { eyebrow: 'WEEK 2 — MEASURE',   body: 'Trial-to-paid conversion threshold: <b>≥ 8%</b>. Below is a messaging problem.' },
    { eyebrow: 'KILLSWITCH',         body: 'CAC payback <b>under 12 months</b>, or pivot — not iterate.' },
  ],
  es: [
    { eyebrow: 'SEMANA 1 — DESCUBRIR', body: '<b>30 llamadas</b> por willingness-to-pay. Clusteriza el top decil.' },
    { eyebrow: 'SEMANA 2 — CERRAR',    body: 'Convierte el top 5 en <b>LOIs pagadas</b> en 14 días. <b>3+ cerradas</b> = señal PMF.' },
    { eyebrow: 'SEMANA 2 — MEDIR',     body: 'Conversión trial-a-pago: <b>≥ 8%</b>. Bajo eso es problema de mensaje.' },
    { eyebrow: 'KILLSWITCH',           body: 'CAC payback <b>bajo 12 meses</b>, o pivota — no iteres.' },
  ],
  pt: [
    { eyebrow: 'SEMANA 1 — DESCOBRIR', body: '<b>30 ligações</b> por disposição a pagar. Agrupe o top decil.' },
    { eyebrow: 'SEMANA 2 — FECHAR',    body: 'Converta top 5 em <b>LOIs pagas</b> em 14 dias. <b>3+ fechadas</b> = sinal PMF.' },
    { eyebrow: 'SEMANA 2 — MEDIR',     body: 'Conversão trial-para-pago: <b>≥ 8%</b>. Abaixo é problema de mensagem.' },
    { eyebrow: 'KILLSWITCH',           body: 'Payback de CAC <b>abaixo de 12 meses</b>, ou pivote — não itere.' },
  ],
};

// Optimal prompt — formatted as a real prompt template with sections
const OPTIMAL = {
  en: [
    { tag: 'ROLE', body: 'B2B SaaS PMF coach · 10 years seed-stage operator.' },
    { tag: 'TASK', body: 'Given founder context, produce a 30-day plan.' },
    { tag: 'STRUCTURE', body: [
      '1. 30-call discovery target, scored on willingness-to-pay',
      '2. 14-day LOI close rate as primary PMF signal',
      '3. Week-2 trial-to-paid threshold (≥ 8%)',
      '4. CAC payback ≤ 12mo as killswitch',
    ]},
    { tag: 'FORMAT', body: '4 bullets, max 40 words each. No fluff.' },
  ],
  es: [
    { tag: 'ROL', body: 'Coach de PMF B2B SaaS · 10 años operativo en seed.' },
    { tag: 'TAREA', body: 'Dado el contexto del fundador, plan de 30 días.' },
    { tag: 'ESTRUCTURA', body: [
      '1. Meta de 30 llamadas, scored por willingness-to-pay',
      '2. Tasa de cierre LOIs en 14 días = señal PMF primaria',
      '3. Umbral semana-2 trial-a-pago (≥ 8%)',
      '4. CAC payback ≤ 12mo como killswitch',
    ]},
    { tag: 'FORMATO', body: '4 viñetas, máx 40 palabras cada una. Sin fluff.' },
  ],
  pt: [
    { tag: 'PAPEL', body: 'Coach de PMF B2B SaaS · 10 anos operacional em seed.' },
    { tag: 'TAREFA', body: 'Dado o contexto do fundador, plano de 30 dias.' },
    { tag: 'ESTRUTURA', body: [
      '1. Meta de 30 ligações, avaliadas por disposição a pagar',
      '2. Taxa de fechamento de LOIs em 14 dias = sinal PMF',
      '3. Limiar semana-2 trial-para-pago (≥ 8%)',
      '4. Payback de CAC ≤ 12mo como killswitch',
    ]},
    { tag: 'FORMATO', body: '4 bullets, máx 40 palavras cada. Sem enrolação.' },
  ],
};

// ============================================================================
// Render helpers
// ============================================================================

function typewriter(target: HTMLElement, text: string, ms: number): void {
  target.textContent = '';
  const chars = Array.from(text);
  let i = 0;
  const step = Math.max(8, Math.floor(ms / chars.length));
  const id = window.setInterval(() => {
    if (i >= chars.length) { window.clearInterval(id); return; }
    target.textContent = (target.textContent || '') + chars[i++];
  }, step);
  (target as any)._twId = id;
}

// ============================================================================
// Screen renderers
// ============================================================================

function renderHook(container: HTMLElement, lang: Lang): void {
  const wrap = document.createElement('div');
  wrap.className = 'mock mock-prompt';
  wrap.innerHTML = `
    <div class="mock-chrome">
      <span class="mock-dot"></span><span class="mock-dot"></span><span class="mock-dot"></span>
      <span class="mock-url">aiboard.localhost:9090</span>
    </div>
    <div class="mock-body">
      <div class="mock-panel-head">
        <span class="mock-panel-title">PROMPT</span>
        <span class="mock-meta"><span class="mock-counter" id="mock-counter">0</span> chars · 5 models</span>
      </div>
      <div class="mock-textarea" id="mock-textarea"></div>
      <div class="mock-footer">
        <span class="mock-hint">⌘+Enter to run</span>
        <button class="mock-run-btn" disabled>▶ Run</button>
      </div>
    </div>
  `;
  container.appendChild(wrap);
  const ta = wrap.querySelector('#mock-textarea') as HTMLElement;
  const counter = wrap.querySelector('#mock-counter') as HTMLElement;
  const runBtn = wrap.querySelector('.mock-run-btn') as HTMLButtonElement;
  const prompt = SAMPLE_PROMPT[lang];
  typewriter(ta, prompt, 4500);
  const watch = window.setInterval(() => {
    const len = (ta.textContent || '').length;
    counter.textContent = String(len);
    if (len > 30) runBtn.disabled = false;
    if (len >= prompt.length) {
      runBtn.classList.add('mock-run-btn-glow');
      window.clearInterval(watch);
    }
  }, 80);
  (wrap as any)._watch = watch;
}

function renderFanout(container: HTMLElement, lang: Lang): void {
  const wrap = document.createElement('div');
  wrap.className = 'mock mock-fanout';
  const labels = {
    en: ['Sending in parallel', 'fan-out · 5 models'],
    es: ['Enviando en paralelo', 'fan-out · 5 modelos'],
    pt: ['Enviando em paralelo', 'fan-out · 5 modelos'],
  }[lang];
  const models = [
    { label: 'Claude',  color: '#e0a85a' },
    { label: 'GPT-4o',  color: '#19c37d' },
    { label: 'Gemini',  color: '#6b8af5' },
    { label: 'Grok',    color: '#f2f0eb' },
    { label: 'Mistral', color: '#ff7a37' },
  ];
  wrap.innerHTML = `
    <div class="mock-fanout-head">
      <span class="mock-panel-title">FAN-OUT</span>
      <span class="mock-meta">${labels[1]}</span>
    </div>
    <div class="mock-fanout-source">
      <div class="mock-fanout-prompt">${SAMPLE_PROMPT[lang]}</div>
      <div class="mock-fanout-status"><span class="mock-pulse-dot"></span> ${labels[0]}…</div>
    </div>
    <div class="mock-fanout-models">
      ${models.map((m) => `
        <div class="mock-model" style="--c:${m.color}">
          <span class="mock-model-dot"></span>
          <span class="mock-model-label">${m.label}</span>
          <span class="mock-model-status">pending</span>
        </div>
      `).join('')}
    </div>
  `;
  container.appendChild(wrap);
  wrap.querySelectorAll<HTMLElement>('.mock-model').forEach((m, i) => {
    m.style.animationDelay = (i * 110) + 'ms';
    m.classList.add('mock-stagger-in');
    // Flip status to "running" after stagger
    window.setTimeout(() => {
      const st = m.querySelector('.mock-model-status');
      if (st) st.textContent = 'running…';
    }, 700 + i * 110);
  });
}

function renderCompare(container: HTMLElement, lang: Lang): void {
  const responses = RESPONSES[lang];
  const wrap = document.createElement('div');
  wrap.className = 'mock mock-compare';
  wrap.innerHTML = `
    <div class="mock-compare-head">
      <span class="mock-panel-title">RESPONSES</span>
      <span class="mock-meta">3 of 5 · click to expand</span>
    </div>
    <div class="mock-grid">
      ${responses.map((r, idx) => `
        <article class="mock-response" style="--c:${MODELS[idx].color}; --i:${idx}">
          <header class="mock-response-head">
            <span class="mock-response-badge" style="background:${MODELS[idx].color}">${MODELS[idx].label}</span>
            <span class="mock-response-time">${MODELS[idx].time}</span>
          </header>
          <div class="mock-response-approach">${r.approach}</div>
          <ul class="mock-response-list">
            ${r.points.map((p) => `<li>${p}</li>`).join('')}
          </ul>
          <footer class="mock-response-foot">
            <span class="mock-cost">${MODELS[idx].cost}</span>
            <span class="mock-tokens">${MODELS[idx].tokens} tok</span>
          </footer>
        </article>
      `).join('')}
    </div>
  `;
  container.appendChild(wrap);
}

function renderSynthesize(container: HTMLElement, lang: Lang): void {
  const wrap = document.createElement('div');
  wrap.className = 'mock mock-synth';
  const blocks = SYNTH[lang];
  const chipLabels = {
    en: ['Executive', 'Pros · Cons', 'Consensus', 'Custom'],
    es: ['Ejecutivo', 'Pros · Contras', 'Consenso', 'Personalizado'],
    pt: ['Executivo', 'Prós · Contras', 'Consenso', 'Personalizado'],
  }[lang];
  const subtitle = {
    en: 'SYNTHESIS · EXECUTIVE SUMMARY',
    es: 'SÍNTESIS · RESUMEN EJECUTIVO',
    pt: 'SÍNTESE · RESUMO EXECUTIVO',
  }[lang];
  wrap.innerHTML = `
    <div class="mock-synth-head">
      <span class="mock-synth-eyebrow">${subtitle}</span>
      <span class="mock-synth-by">via claude-opus-5</span>
    </div>
    <div class="mock-synth-grid">
      ${blocks.map((b) => `
        <div class="mock-synth-block">
          <div class="mock-synth-block-eyebrow">${b.eyebrow}</div>
          <div class="mock-synth-block-body">${b.body}</div>
        </div>
      `).join('')}
    </div>
    <div class="mock-synth-foot">
      ${chipLabels.map((label, i) => `<button class="mock-chip${i === 0 ? ' mock-chip-on' : ''}">${label}</button>`).join('')}
    </div>
  `;
  container.appendChild(wrap);
}

function renderOptimal(container: HTMLElement, lang: Lang): void {
  const wrap = document.createElement('div');
  wrap.className = 'mock mock-optimal';
  const blocks = OPTIMAL[lang];
  const subtitle = {
    en: 'OPTIMAL PROMPT · REUSABLE',
    es: 'PROMPT ÓPTIMO · REUTILIZABLE',
    pt: 'PROMPT IDEAL · REUTILIZÁVEL',
  }[lang];
  const meta = {
    en: 'engineered from your synthesis',
    es: 'engineered desde tu síntesis',
    pt: 'engenharia reversa da sua síntese',
  }[lang];
  const labels = {
    en: { copy: 'Copy', save: 'Save', use: 'Use this' },
    es: { copy: 'Copiar', save: 'Guardar', use: 'Usar' },
    pt: { copy: 'Copiar', save: 'Salvar', use: 'Usar' },
  }[lang];

  const body = blocks.map((b) => {
    if (Array.isArray(b.body)) {
      return `<div class="mock-optimal-block"><span class="mock-optimal-tag">${b.tag}</span>` +
             b.body.map((line) => `<div class="mock-optimal-line">${line}</div>`).join('') +
             `</div>`;
    }
    return `<div class="mock-optimal-block"><span class="mock-optimal-tag">${b.tag}</span><span class="mock-optimal-text">${b.body}</span></div>`;
  }).join('');

  wrap.innerHTML = `
    <div class="mock-optimal-head">
      <span class="mock-optimal-eyebrow">${subtitle}</span>
      <span class="mock-optimal-meta">${meta}</span>
    </div>
    <div class="mock-optimal-body">${body}</div>
    <div class="mock-optimal-foot">
      <button class="mock-mini-btn">📋 ${labels.copy}</button>
      <button class="mock-mini-btn">💾 ${labels.save}</button>
      <button class="mock-mini-btn mock-mini-btn-primary">▶ ${labels.use}</button>
    </div>
  `;
  container.appendChild(wrap);
}

function renderPricing(container: HTMLElement, lang: Lang): void {
  const wrap = document.createElement('div');
  wrap.className = 'mock mock-pricing';

  // ----- i18n strings (centralized) -----
  const T = {
    en: {
      title: 'Pricing',
      sub: 'BYOK · cancel anytime · no markup',
      monthly: 'Monthly',
      yearly: 'Yearly',
      save: '−17%',
      yearlyHint: (mo: number) => `or $${mo * 10}/yr · save ~17%`,
      tagFree: 'Try the demo',
      tagPro: 'For solo operators',
      tagTeam: 'For multiplayer teams',
      ctaFree: 'Always free',
      ctaPro: 'Start 14-day trial',
      ctaTeam: 'Start trial',
      ctaProHint: 'No card · cancel anytime',
      ribbon: 'MOST POPULAR',
      feats: {
        free: [['demo','Demo mode'], ['compare','Side-by-side compare'], ['off','No BYOK'], ['off','No history']],
        pro:  [['ok','BYOK · all 5 models'], ['ok','Compare history'], ['ok','DOCX · PDF · PPTX exports'], ['ok','Cloud-side synthesis'], ['ok','Optimal Prompt generator']],
        team: [['ok','Everything in Pro'], ['ok','Shared workspaces'], ['ok','White-label'], ['ok','Priority support']],
      },
      trust: ['Keys stay in your browser', 'Pay providers directly', 'Cancel anytime', 'Available worldwide'],
    },
    es: {
      title: 'Precios',
      sub: 'BYOK · cancela cuando quieras · sin margen',
      monthly: 'Mensual',
      yearly: 'Anual',
      save: '−17%',
      yearlyHint: (mo: number) => `o $${mo * 10}/año · ahorra ~17%`,
      tagFree: 'Prueba la demo',
      tagPro: 'Para operadores solos',
      tagTeam: 'Para equipos',
      ctaFree: 'Siempre gratis',
      ctaPro: 'Prueba 14 días',
      ctaTeam: 'Prueba 14 días',
      ctaProHint: 'Sin tarjeta · cancelas cuando quieras',
      ribbon: 'EL FAVORITO',
      feats: {
        free: [['demo','Modo demo'], ['compare','Compare lado a lado'], ['off','Sin BYOK'], ['off','Sin historial']],
        pro:  [['ok','BYOK · los 5 modelos'], ['ok','Historial de compares'], ['ok','Exports DOCX · PDF · PPTX'], ['ok','Síntesis en la nube'], ['ok','Optimal Prompt']],
        team: [['ok','Todo de Pro'], ['ok','Workspaces compartidos'], ['ok','White-label'], ['ok','Soporte prioritario']],
      },
      trust: ['Llaves en tu navegador', 'Pagas a proveedores directo', 'Cancela cuando quieras', 'Disponible mundial'],
    },
    pt: {
      title: 'Preços',
      sub: 'BYOK · cancele quando quiser · sem margem',
      monthly: 'Mensal',
      yearly: 'Anual',
      save: '−17%',
      yearlyHint: (mo: number) => `ou US$ ${mo * 10}/ano · economize ~17%`,
      tagFree: 'Experimente a demo',
      tagPro: 'Para operadores solo',
      tagTeam: 'Para equipes',
      ctaFree: 'Sempre grátis',
      ctaPro: 'Teste 14 dias',
      ctaTeam: 'Teste 14 dias',
      ctaProHint: 'Sem cartão · cancele quando quiser',
      ribbon: 'MAIS POPULAR',
      feats: {
        free: [['demo','Modo demo'], ['compare','Compare lado a lado'], ['off','Sem BYOK'], ['off','Sem histórico']],
        pro:  [['ok','BYOK · 5 modelos'], ['ok','Histórico de compares'], ['ok','Exports DOCX · PDF · PPTX'], ['ok','Síntese em nuvem'], ['ok','Optimal Prompt']],
        team: [['ok','Tudo do Pro'], ['ok','Workspaces compartilhados'], ['ok','White-label'], ['ok','Suporte prioritário']],
      },
      trust: ['Chaves no seu navegador', 'Pague provedores direto', 'Cancele quando quiser', 'Disponível mundial'],
    },
  }[lang];

  const TIERS = [
    { key: 'free', name: 'Free', tag: T.tagFree, mo: 0,  cta: T.ctaFree, ctaKind: 'ghost',     hint: '',          feats: T.feats.free },
    { key: 'pro',  name: 'Pro',  tag: T.tagPro,  mo: 19, cta: T.ctaPro,  ctaKind: 'primary',   hint: T.ctaProHint, feats: T.feats.pro,  featured: true },
    { key: 'team', name: 'Team', tag: T.tagTeam, mo: 49, cta: T.ctaTeam, ctaKind: 'secondary', hint: '',           feats: T.feats.team },
  ];

  // SVG icon for "included" — matrix-green check inside a soft halo
  const checkSvg = `
    <svg class="feat-ico feat-ico-ok" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="7" fill="rgba(0,255,156,0.12)" />
      <path d="M4.5 8.4 L7 11 L11.6 5.4" fill="none" stroke="#00FF9C" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
    </svg>`;
  const xSvg = `
    <svg class="feat-ico feat-ico-off" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="7" fill="rgba(255,255,255,0.06)" />
      <path d="M5.5 5.5 L10.5 10.5 M10.5 5.5 L5.5 10.5" stroke="rgba(242,240,235,0.45)" stroke-width="1.6" stroke-linecap="round" />
    </svg>`;
  const demoSvg = `
    <svg class="feat-ico feat-ico-demo" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="7" fill="rgba(252,226,138,0.12)" />
      <polygon points="6,5 11,8 6,11" fill="#FCE28A" />
    </svg>`;
  const compareSvg = `
    <svg class="feat-ico feat-ico-demo" viewBox="0 0 16 16" aria-hidden="true">
      <circle cx="8" cy="8" r="7" fill="rgba(127,255,212,0.10)" />
      <rect x="4.5" y="5" width="2.6" height="6" rx="0.5" fill="#7FFFD4"/>
      <rect x="8.9" y="5" width="2.6" height="6" rx="0.5" fill="#7FFFD4"/>
    </svg>`;

  const ico = (kind: string): string => {
    if (kind === 'ok') return checkSvg;
    if (kind === 'off') return xSvg;
    if (kind === 'compare') return compareSvg;
    return demoSvg;
  };

  const tierCard = (t: typeof TIERS[number]): string => `
    <article class="mock-tier ${t.featured ? 'mock-tier-featured' : ''}">
      ${t.featured ? `<div class="mock-tier-ribbon">${T.ribbon}</div>` : ''}
      <header class="mock-tier-head">
        <div class="mock-tier-name">${t.name}</div>
        <div class="mock-tier-tag">${t.tag}</div>
      </header>
      <div class="mock-tier-price-block">
        <span class="mock-tier-curr">$</span>
        <span class="mock-tier-price">${t.mo}</span>
        <span class="mock-tier-cycle">/${lang === 'es' ? 'mes' : lang === 'pt' ? 'mês' : 'mo'}</span>
      </div>
      <div class="mock-tier-yearly">${t.mo > 0 ? T.yearlyHint(t.mo) : '&nbsp;'}</div>
      <ul class="mock-tier-feats">
        ${t.feats.map(([kind, label]) => `
          <li class="${kind === 'off' ? 'mock-tier-feat-off' : ''}">
            ${ico(kind)}<span>${label}</span>
          </li>
        `).join('')}
      </ul>
      <footer class="mock-tier-foot">
        <button class="mock-tier-cta mock-tier-cta-${t.ctaKind}">${t.cta}</button>
        ${t.hint ? `<small class="mock-tier-hint">${t.hint}</small>` : ''}
      </footer>
    </article>
  `;

  wrap.innerHTML = `
    <header class="mock-pricing-head">
      <div class="mock-pricing-title-block">
        <span class="mock-pricing-eyebrow">PRICING</span>
        <h3 class="mock-pricing-title">${T.title}</h3>
        <p class="mock-pricing-sub">${T.sub}</p>
      </div>
      <div class="mock-cycle-toggle" role="tablist" aria-label="Billing cycle">
        <button class="mock-cycle-opt mock-cycle-opt-on" type="button">${T.monthly}</button>
        <button class="mock-cycle-opt" type="button">${T.yearly} <span class="mock-cycle-save">${T.save}</span></button>
      </div>
    </header>
    <div class="mock-pricing-grid">
      ${TIERS.map(tierCard).join('')}
    </div>
    <footer class="mock-pricing-trust">
      ${T.trust.map((t) => `<span class="mock-pricing-trust-item">${t}</span>`).join('<span class="mock-pricing-trust-sep">·</span>')}
    </footer>
  `;
  container.appendChild(wrap);
}

function renderCta(container: HTMLElement, lang: Lang): void {
  const wrap = document.createElement('div');
  wrap.className = 'mock mock-cta';
  const copy = {
    en: { headline: 'Your trial is one email away.', sub: 'No card. Cancel anytime. Exports stay yours.', primary: 'Start free →', secondary: 'See pricing' },
    es: { headline: 'Tu prueba a un correo de distancia.', sub: 'Sin tarjeta. Cancela cuando quieras. Tus exports son tuyos.', primary: 'Empezar gratis →', secondary: 'Ver precios' },
    pt: { headline: 'Seu teste a um e-mail de distância.', sub: 'Sem cartão. Cancele quando quiser. Seus exports são seus.', primary: 'Começar grátis →', secondary: 'Ver preços' },
  }[lang];
  wrap.innerHTML = `
    <div class="mock-cta-glyph"><span class="brand-iris" aria-hidden="true"></span></div>
    <div class="mock-cta-headline">${copy.headline}</div>
    <div class="mock-cta-sub">${copy.sub}</div>
    <div class="mock-cta-row">
      <a class="mock-cta-primary" href="/register.html">${copy.primary}</a>
      <a class="mock-cta-secondary" href="/pricing.html">${copy.secondary}</a>
    </div>
    <div class="mock-cta-meta">
      <span class="mock-cta-tag">14 days</span>
      <span class="mock-cta-tag">no card</span>
      <span class="mock-cta-tag">cancel anytime</span>
      <span class="mock-cta-tag">BYOK</span>
    </div>
  `;
  container.appendChild(wrap);
}

// ============================================================================
// Public API
// ============================================================================

export function showScreen(key: ScreenKey, container: HTMLElement, lang: Lang): void {
  container.querySelectorAll('*').forEach((el) => {
    const id = (el as any)._twId;
    const watch = (el as any)._watch;
    if (id) window.clearInterval(id);
    if (watch) window.clearInterval(watch);
  });
  container.innerHTML = '';
  void container.offsetWidth;

  switch (key) {
    case 'hook':       return renderHook(container, lang);
    case 'fanout':     return renderFanout(container, lang);
    case 'compare':    return renderCompare(container, lang);
    case 'synthesize': return renderSynthesize(container, lang);
    case 'optimal':    return renderOptimal(container, lang);
    case 'pricing':    return renderPricing(container, lang);
    case 'cta':        return renderCta(container, lang);
  }
}
